import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Input, Select } from '@surecompanies/core_components'
import FilterOutlined from '@ant-design/icons/FilterOutlined'
import SearchOutlined from '@ant-design/icons/SearchOutlined'
import { useDebounce } from 'hooks/useDebounce'
import { US_STATE_OPTIONS } from '../constants'
import styles from './PlanOverridesComponents.module.scss'

const DEBOUNCE_MS = 300

interface Props {
  state: string | undefined
  issuerId: string | undefined
  hiosPlanId: string | undefined
  onStateChange: (value: string | undefined) => void
  onIssuerIdChange: (value: string) => void
  onHiosPlanIdChange: (value: string) => void
  onClearAll: () => void
}

export default function GlobalOverrideFilter({
  state,
  issuerId,
  hiosPlanId,
  onStateChange,
  onIssuerIdChange,
  onHiosPlanIdChange,
  onClearAll,
}: Props) {
  const [issuerInput, setIssuerInput] = useState(issuerId ?? '')
  const [hiosInput, setHiosInput] = useState(hiosPlanId ?? '')

  const debouncedIssuer = useDebounce(issuerInput, DEBOUNCE_MS)
  const debouncedHios = useDebounce(hiosInput, DEBOUNCE_MS)

  const lastPushedIssuerRef = useRef(issuerId ?? '')
  const lastPushedHiosRef = useRef(hiosPlanId ?? '')

  useEffect(() => {
    if (debouncedIssuer === lastPushedIssuerRef.current) return
    lastPushedIssuerRef.current = debouncedIssuer
    onIssuerIdChange(debouncedIssuer)
  }, [debouncedIssuer, onIssuerIdChange])

  useEffect(() => {
    if (debouncedHios === lastPushedHiosRef.current) return
    lastPushedHiosRef.current = debouncedHios
    onHiosPlanIdChange(debouncedHios)
  }, [debouncedHios, onHiosPlanIdChange])

  useEffect(() => {
    lastPushedIssuerRef.current = issuerId ?? ''
    setIssuerInput(issuerId ?? '')
  }, [issuerId])

  useEffect(() => {
    lastPushedHiosRef.current = hiosPlanId ?? ''
    setHiosInput(hiosPlanId ?? '')
  }, [hiosPlanId])

  const handleClear = useCallback(() => {
    setIssuerInput('')
    setHiosInput('')
    lastPushedIssuerRef.current = ''
    lastPushedHiosRef.current = ''
    onClearAll()
  }, [onClearAll])

  const hasAnyFilter = !!state || !!issuerInput || !!hiosInput

  return (
    <div className={styles.globalFilterBar}>
      <span className={styles.globalFilterLabel}>
        <FilterOutlined />
        Filter
      </span>

      <div className={styles.globalFilterInputs}>
        <Select
          allowClear
          showSearch
          placeholder="Nationwide — filter by state(s)"
          value={state ?? null}
          onChange={onStateChange}
          options={US_STATE_OPTIONS}
          optionFilterProp="label"
          style={{ width: 260 }}
        />
        <Input
          prefix={<SearchOutlined />}
          placeholder="Issuer ID"
          value={issuerInput}
          allowClear
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setIssuerInput(e.target.value)
          }
          style={{ width: 180 }}
        />
        <Input
          prefix={<SearchOutlined />}
          placeholder="HIOS Plan ID"
          value={hiosInput}
          allowClear
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setHiosInput(e.target.value)
          }
          style={{ width: 260 }}
        />
      </div>

      {hasAnyFilter && (
        <Button
          type="link"
          onClick={handleClear}
          className={styles.globalFilterClear}
        >
          &times; Clear filters
        </Button>
      )}
    </div>
  )
}
